'use client';

import React, {
    ButtonHTMLAttributes,
    forwardRef,
    JSXElementConstructor,
    useRef,
} from 'react';
import cn from 'clsx';
import s from './size-selector.module.css';

export type SizeState =
    | 'default'
    | 'selected'
    | 'outOfStock'
    | 'outOfStockSelected';

export interface SizeSelectorProps
    extends ButtonHTMLAttributes<HTMLButtonElement> {
    state: SizeState;
    loading?: boolean;
    disabled?: boolean;
    Component?: string | JSXElementConstructor<any>;
    type?: 'submit' | 'reset' | 'button';
    className?: string;
    hrefLink?: string;
    label?: string;
    hasLongLabel?: boolean;
}

export const SizeSelector: React.FC<SizeSelectorProps> = forwardRef(
    (props, buttonRef) => {
        const {
            state = 'default',
            children,
            loading = false,
            disabled = false,
            Component = 'button',
            className,
            hrefLink,
            label,
            hasLongLabel = false,
            onClick,
            ...rest
        } = props;
        const ref = useRef<typeof Component>(null);
        const rootClassName = cn(
            s.root,
            {
                [s.default]: state === 'default',
                [s.selected]: state === 'selected',
                [s.outOfStock]: state === 'outOfStock',
                [s.outOfStockSelected]: state === 'outOfStockSelected',
                [s.loading]: loading,
                [s.disabled]: disabled,
                [s.hasLongLabel]: hasLongLabel,
            },
            className
        );

        return (
            <>
                {hrefLink ? (
                    <a href={hrefLink} className={s.buttonLink}>
                        <Component
                            aria-pressed={state === 'selected'}
                            data-variant={state}
                            disabled={disabled}
                            className={rootClassName}
                            {...rest}
                        >
                            {label}
                        </Component>
                    </a>
                ) : (
                    <Component
                        aria-pressed={state === 'selected'}
                        data-variant={state}
                        disabled={disabled}
                        className={rootClassName}
                        onClick={onClick}
                        {...rest}
                    >
                        {label}
                    </Component>
                )}
            </>
        );
    }
);

SizeSelector.displayName = 'SizeSelector';
export default SizeSelector;
